import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { SortableList } from '../../components/SortableList';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyState, Spinner } from '../../components/ui';
import { BASE, LINKED, MembershipArray, keys, statusTone } from './api';
import type { SrBuilding, SrMembership } from './api';

/** The order companies appear in on the shared kiosk. */
export function OrderTab({ building, canManage }: { building: SrBuilding; canManage: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: keys.memberships(building.id), queryFn: () => api.get(`${BASE}/${building.id}/memberships`, { schema: MembershipArray }) });
  const [items, setItems] = useState<SrMembership[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!q.data) return;
    setItems(q.data.filter((m) => LINKED.includes(m.status)).sort((a, b) => a.sort - b.sort));
    setDirty(false);
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put(`${BASE}/${building.id}/order`, { body: { membershipIds: items.map((m) => m.id) } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.memberships(building.id) });
      toast(t('srp.order.saved'));
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const label = (m: SrMembership) => m.displayName ?? m.tenantName ?? m.tenantId;
  return (
    <Card
      title={t('srp.order.list')}
      description={t('srp.order.hint')}
      footer={
        canManage ? (
          <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {t('srp.order.save')}
          </Button>
        ) : null
      }
    >
      <ErrorLine error={save.error} />
      {items.length === 0 ? (
        <EmptyState message={t('srp.order.empty')} />
      ) : (
        <SortableList
          items={items}
          label={t('srp.order.list')}
          disabled={!canManage}
          getKey={(m) => m.id}
          getLabel={label}
          onChange={(next) => {
            setItems(next);
            setDirty(true);
          }}
          renderItem={(m) => (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{label(m)}</span>
              <Badge tone={statusTone(m.status)}>{t(`sr.status.${m.status}`)}</Badge>
            </span>
          )}
        />
      )}
    </Card>
  );
}
